import React from "react";
import { Droppable } from "@hello-pangea/dnd";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, Tooltip, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import BlockIcon from "@material-ui/icons/Block";
import InputIcon from "@material-ui/icons/Input";
import { i18n } from "../../translate/i18n";
import BoardCard from "./BoardCard";

// feat_395 — una columna del Tablero CRM. Header del color del estado; el
// cuerpo scrollea solo. Columnas inactivas (`active:false`) se ven atenuadas y
// no aceptan drop.
// feat_397 — la columna de la etapa de entrada (`isEntry`) recibe a los
// contactos sin etapa: se marca con un borde del color del estado + chip
// "Entrada". Su contador es el total real (COUNT del backend).

const NO_STATE_COLOR = "#8696a0";

// Texto blanco o casi negro según la luminancia del color del estado.
const readableOn = hex => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ""));
  if (!m) return "#ffffff";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? "#111b21" : "#ffffff";
};

const isDark = theme => theme.palette.type === "dark";

const useStyles = makeStyles(theme => ({
  column: {
    flex: `0 0 ${theme.spacing(37)}px`,
    width: theme.spacing(37),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    maxHeight: "100%",
    borderRadius: theme.spacing(1),
    background: isDark(theme) ? "#111b21" : "#f0f2f5",
    overflow: "hidden",
  },
  inactive: { opacity: 0.6 },
  entry: { borderWidth: 2, borderStyle: "solid" },
  entryChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    fontWeight: 700,
    fontSize: "0.7em",
    textTransform: "uppercase",
    padding: theme.spacing(0, 0.75),
    borderRadius: theme.spacing(1.5),
    background: "rgba(0,0,0,.18)",
    flexShrink: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    flexShrink: 0,
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  total: {
    fontWeight: 700,
    fontSize: "0.8em",
    padding: theme.spacing(0, 1),
    borderRadius: theme.spacing(1.5),
    background: "rgba(0,0,0,.18)",
  },
  body: {
    flex: 1,
    minHeight: theme.spacing(10),
    overflowY: "auto",
    padding: theme.spacing(1),
    transition: "background .15s",
    ...theme.scrollbarStyles,
  },
  over: {
    background: isDark(theme) ? "rgba(37,211,102,.08)" : "rgba(37,211,102,.12)",
  },
  empty: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 0),
    fontSize: "0.85em",
  },
  more: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(0.5, 0, 1),
  },
  skeleton: {
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
}));

const BoardColumn = ({
  column,
  dropDisabled,
  loading,
  loadingMore,
  onLoadMore,
  avatarSize,
  now,
  onOpenCard,
  onCardMenu,
}) => {
  const classes = useStyles();
  const color = column.stateId === null ? NO_STATE_COLOR : column.color || NO_STATE_COLOR;
  const name = column.stateId === null ? i18n.t("crmBoard.noState") : column.name;
  const inactive = column.active === false;
  const isEntry = !!column.isEntry;

  const header = (
    <div className={classes.header} style={{ background: color, color: readableOn(color) }}>
      {inactive && <BlockIcon fontSize="small" />}
      <Typography variant="subtitle2" component="h2" className={classes.title}>
        {name}
      </Typography>
      {isEntry && (
        <Tooltip title={i18n.t("crmBoard.entryTooltip")} arrow>
          <span className={classes.entryChip} data-entry-chip>
            <InputIcon style={{ fontSize: "1.1em" }} />
            {i18n.t("crmBoard.entryBadge")}
          </span>
        </Tooltip>
      )}
      <span className={classes.total}>{column.total}</span>
    </div>
  );

  return (
    <section
      className={`${classes.column} ${inactive ? classes.inactive : ""} ${isEntry ? classes.entry : ""}`}
      style={isEntry ? { borderColor: color } : undefined}
      aria-label={name}
      data-column-key={column.key}
      data-entry={isEntry ? "true" : undefined}
    >
      {inactive ? (
        <Tooltip title={i18n.t("crmBoard.inactiveTooltip")} arrow>
          {header}
        </Tooltip>
      ) : (
        header
      )}
      <Droppable droppableId={column.key} isDropDisabled={inactive || dropDisabled}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`${classes.body} ${snapshot.isDraggingOver ? classes.over : ""}`}
          >
            {loading && column.cards.length === 0
              ? [0, 1, 2].map(i => (
                  <Skeleton key={i} variant="rect" height={avatarSize + 24} className={classes.skeleton} />
                ))
              : column.cards.map((card, index) => (
                  <BoardCard
                    key={card.contact.id}
                    card={card}
                    index={index}
                    avatarSize={avatarSize}
                    now={now}
                    onOpen={onOpenCard}
                    onMenu={onCardMenu}
                  />
                ))}
            {provided.placeholder}
            {!loading && column.cards.length === 0 && (
              <div className={classes.empty}>{i18n.t("crmBoard.emptyColumn")}</div>
            )}
            {column.hasMore && (
              <div className={classes.more}>
                <Button size="small" onClick={() => onLoadMore(column.key)} disabled={!!loadingMore}>
                  {loadingMore ? <CircularProgress size={16} /> : i18n.t("crmBoard.loadMore")}
                </Button>
              </div>
            )}
          </div>
        )}
      </Droppable>
    </section>
  );
};

export default React.memo(BoardColumn);
